import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const Icon = ({ name, size = 20 }) => {
  const base = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', width: size, height: size, 'aria-hidden': true };
  switch (name) {
    case 'doc': return <svg {...base}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /></svg>;
    case 'check-circle': return <svg {...base}><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 4.5-5" /></svg>;
    case 'circle': return <svg {...base}><circle cx="12" cy="12" r="9" /></svg>;
    case 'chevron-down': return <svg {...base}><path d="m6 9 6 6 6-6" /></svg>;
    case 'chevron-up': return <svg {...base}><path d="m6 15 6-6 6 6" /></svg>;
    case 'chevron-right': return <svg {...base}><path d="m9 6 6 6-6 6" /></svg>;
    case 'search': return <svg {...base}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>;
    case 'mail': return <svg {...base}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>;
    case 'download': return <svg {...base}><path d="M12 3v12" /><path d="m7 11 5 5 5-5" /><path d="M4 21h16" /></svg>;
    case 'square-check': return <svg {...base}><rect x="4" y="4" width="16" height="16" rx="3" /><path d="m8.5 12 2.5 2.5 4.5-5" /></svg>;
    case 'square': return <svg {...base}><rect x="4" y="4" width="16" height="16" rx="3" /></svg>;
    case 'radio-on': return <svg {...base}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" /></svg>;
    default: return null;
  }
};

const STATUS_COLOR = {
  valid: 'var(--cr-green)',
  expiring: 'var(--cr-amber)',
  expired: 'var(--cr-red)',
  no_date: 'var(--cr-secondary)'
};

const fmtDate = v => (v ? String(v).slice(0, 10) : '');

const Tile = ({ color, icon }) => (
  <div style={{ width: 40, height: 40, borderRadius: 11, background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    <Icon name={icon} size={20} />
  </div>
);

const SectionLabel = ({ children }) => <div className="section-label" style={{ margin: '14px 2px 6px' }}>{children}</div>;

export default function PacketBuilder() {
  const [meta, setMeta] = useState({ horses: [], secretaries: [], shows: [], packets: [] });
  const [loading, setLoading] = useState(true);
  const [horseIds, setHorseIds] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(true);
  const [search, setSearch] = useState('');
  const [showId, setShowId] = useState('');
  const [format, setFormat] = useState('per_horse');
  const [entries, setEntries] = useState([]);
  const [reference, setReference] = useState({ mode: 'today' });
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [title, setTitle] = useState('');
  const [building, setBuilding] = useState(false);
  const [built, setBuilt] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ secretary_id: '', to_email: '', subject: '', body: '' });
  const [secForm, setSecForm] = useState({ open: false, name: '', email: '', phone: '', organization: '', saving: false });
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState('');

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get('/packets/meta');
      setMeta(data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load packet builder');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const loadSources = useCallback(async (ids, show) => {
    if (!ids.length) { setEntries([]); setSelected({}); return; }
    setSourcesLoading(true);
    try {
      const params = { horse_ids: ids.join(',') };
      if (show) params.show_id = show;
      const { data } = await api.get('/packets/sources', { params });
      setEntries(data.entries || []);
      setReference(data.reference || { mode: 'today' });
      const next = {};
      (data.entries || []).forEach(e => { next[e.key] = e.files.length > 0 && e.expiry?.status !== 'expired'; });
      setSelected(next);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load documents');
    } finally {
      setSourcesLoading(false);
    }
  }, []);
  useEffect(() => { loadSources(horseIds, showId); }, [horseIds, showId, loadSources]);

  const toggleHorse = id => {
    setBuilt(null);
    setHorseIds(prev => (prev.includes(id) ? prev.filter(h => h !== id) : [...prev, id]));
  };
  const allSelected = meta.horses.length > 0 && horseIds.length === meta.horses.length;
  const selectAll = () => { setBuilt(null); setHorseIds(allSelected ? [] : meta.horses.map(h => h.id)); };

  const pickerLabel = !horseIds.length ? 'Choose horses'
    : allSelected ? `All ${meta.horses.length} horses`
    : horseIds.length === 1 ? (meta.horses.find(h => h.id === horseIds[0])?.name || '1 horse selected')
    : `${horseIds.length} horses selected`;

  const filteredHorses = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return meta.horses;
    return meta.horses.filter(h => h.name.toLowerCase().includes(q));
  }, [meta.horses, search]);

  const toggleEntry = entry => {
    if (!entry.files.length) return;
    const next = !selected[entry.key];
    if (next && entry.expiry?.status === 'expired') {
      toast('Including an EXPIRED document — make sure the secretary is OK with it.', { icon: '⚠️' });
    }
    setSelected(prev => ({ ...prev, [entry.key]: next }));
  };

  const selectedEntries = useMemo(() => entries.filter(e => selected[e.key]), [entries, selected]);

  const buildPacket = async () => {
    if (!selectedEntries.length) { toast.error('Select at least one document.'); return; }
    setBuilding(true);
    setBuilt(null);
    setEmailOpen(false);
    try {
      const { data } = await api.post('/packets/build', {
        title: title || undefined,
        horse_ids: horseIds,
        show_id: showId || undefined,
        format,
        selections: selectedEntries.map(e => ({ key: e.key }))
      });
      setBuilt(data);
      setSendForm(f => ({ ...f, subject: `Entry Documents — ${title || data.packets[0].title}` }));
      toast.success(`Built ${data.packets.length} PDF${data.packets.length === 1 ? '' : 's'}`);
      loadMeta();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to build packet');
    } finally {
      setBuilding(false);
    }
  };

  const downloadPacket = async packet => {
    setBusyId(packet.id);
    try {
      const res = await api.get(`/packets/${packet.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = packet.file_name || 'packet.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 410 ? 'Packet file no longer exists on the server.' : 'Download failed');
    } finally {
      setBusyId('');
    }
  };

  const sendPackets = async () => {
    if (!built?.packets?.length) return;
    if (!sendForm.secretary_id && !sendForm.to_email.trim()) {
      toast.error('Choose a secretary or enter an email address.');
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post('/packets/send', {
        packet_ids: built.packets.map(p => p.id),
        secretary_id: sendForm.secretary_id || undefined,
        to_email: sendForm.to_email.trim() || undefined,
        subject: sendForm.subject.trim() || undefined,
        body: sendForm.body.trim() || undefined
      });
      if (data.status === 'sent') toast.success(data.message);
      else toast.error(data.message || 'Not sent.');
      loadMeta();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const addSecretary = async () => {
    if (!secForm.name.trim() || !secForm.email.trim()) { toast.error('Secretary name and email are required.'); return; }
    setSecForm(f => ({ ...f, saving: true }));
    try {
      const { data } = await api.post('/show-center/secretaries', {
        name: secForm.name.trim(),
        email: secForm.email.trim(),
        phone: secForm.phone.trim() || undefined,
        organization: secForm.organization.trim() || undefined
      });
      toast.success(`${data.name} saved to your Secretaries list.`);
      setSecForm({ open: false, name: '', email: '', phone: '', organization: '', saving: false });
      setSendForm(f => ({ ...f, secretary_id: data.id, to_email: data.email || f.to_email }));
      loadMeta();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save secretary');
      setSecForm(f => ({ ...f, saving: false }));
    }
  };

  const deletePacket = async packet => {
    if (!window.confirm(`Delete packet "${packet.title}"? This removes the PDF.`)) return;
    setBusyId(packet.id);
    try {
      await api.delete(`/packets/${packet.id}`);
      toast.success('Packet deleted');
      loadMeta();
    } catch (e) {
      toast.error('Failed to delete');
    } finally {
      setBusyId('');
    }
  };

  const groupedEntries = useMemo(() => {
    const groups = new Map();
    entries.forEach(e => {
      const k = e.horse_name || 'Rider / Owner documents';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(e);
    });
    return Array.from(groups.entries());
  }, [entries]);

  if (loading) return <div style={{ padding: 20, color: 'var(--text-secondary)' }}>Loading packet builder…</div>;

  return (
    <div>
      <div className="card" style={{ background: 'var(--cr-amber-light)', borderColor: 'var(--cr-amber)' }}>
        <div style={{ fontSize: 13, color: 'var(--cr-amber)' }}>
          Expiration checks are estimates — <b>always review the finished packet before sending it to a secretary.</b>
        </div>
      </div>

      <SectionLabel>Horses</SectionLabel>
      <button className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', cursor: 'pointer', background: 'var(--bg-primary)' }} onClick={() => setPickerOpen(o => !o)}>
        <div style={{ color: 'var(--cr-primary)' }}><Icon name="doc" size={20} /></div>
        <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{pickerLabel}</div>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>tap to change</span>
        <div style={{ color: 'var(--text-muted)' }}><Icon name={pickerOpen ? 'chevron-up' : 'chevron-down'} size={18} /></div>
      </button>

      {pickerOpen && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border-color)' }}>
            <div style={{ color: 'var(--text-muted)' }}><Icon name="search" size={16} /></div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search horses" style={{ border: 'none', outline: 'none', flex: 1, fontSize: 14, background: 'transparent', color: 'var(--text-primary)' }} />
          </div>
          <button onClick={selectAll} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none', borderBottom: '1px solid var(--border-color)', background: 'var(--cr-primary-light)', cursor: 'pointer' }}>
            <div style={{ color: 'var(--cr-primary)' }}><Icon name={allSelected ? 'square-check' : 'square'} size={19} /></div>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--cr-primary)' }}>Select all ({meta.horses.length})</span>
            {horseIds.length > 0 && <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>{horseIds.length} selected</span>}
          </button>
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {filteredHorses.map(h => (
              <button key={h.id} onClick={() => toggleHorse(h.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-primary)', cursor: 'pointer' }}>
                <div style={{ color: horseIds.includes(h.id) ? 'var(--cr-primary)' : 'var(--text-muted)' }}><Icon name={horseIds.includes(h.id) ? 'square-check' : 'square'} size={19} /></div>
                <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>{h.name}</span>
              </button>
            ))}
            {!filteredHorses.length && <div style={{ padding: 14, fontSize: 13, color: 'var(--text-secondary)' }}>No horses match "{search}".</div>}
          </div>
          <button onClick={() => setPickerOpen(false)} style={{ display: 'block', width: '100%', padding: 12, border: 'none', background: 'var(--cr-primary)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Done</button>
        </div>
      )}

      {horseIds.length > 0 && (
        <>
          <SectionLabel>Check validity against</SectionLabel>
          <div className="card">
            <select className="form-input" value={showId} onChange={e => { setBuilt(null); setShowId(e.target.value); }}>
              <option value="">Today's date</option>
              {(meta.shows || []).map(s => (
                <option key={s.id} value={s.id}>{s.show_name}{s.start_date ? ` (${fmtDate(s.start_date)})` : ''}</option>
              ))}
            </select>
            {reference.mode === 'show' && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Validity is checked through {fmtDate(reference.end_date || reference.start_date)}.</div>
            )}
          </div>

          <SectionLabel>Documents{horseIds.length > 1 ? ` · ${horseIds.length} horses` : ''}</SectionLabel>
          {sourcesLoading && <div className="card" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Checking both document stores…</div>}
          {!sourcesLoading && !entries.length && (
            <div className="card" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Nothing on file for the selected horse(s). Upload documents in the Documents section or the Credentials tab.</div>
          )}
          {!sourcesLoading && groupedEntries.map(([groupName, groupEntries]) => (
            <div key={groupName}>
              {groupedEntries.length > 1 && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '8px 2px 6px' }}>{groupName}</div>}
              {groupEntries.map(entry => {
                const color = STATUS_COLOR[entry.expiry?.status] || STATUS_COLOR.no_date;
                const on = !!selected[entry.key];
                return (
                  <button key={entry.key} onClick={() => toggleEntry(entry)} className="card" style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left', cursor: entry.files.length ? 'pointer' : 'not-allowed', opacity: entry.files.length ? 1 : 0.55, background: 'var(--bg-primary)' }}>
                    <Tile color={color} icon="doc" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{entry.label}</div>
                      <div style={{ fontSize: 12, color }}>
                        {entry.expiry?.detail}{entry.files.length > 1 ? ` · ${entry.files.map(f => f.role).join(' + ')}` : ''}
                      </div>
                      {entry.warnings.map((w, i) => (
                        <div key={i} style={{ fontSize: 12, color: 'var(--cr-amber)' }}>{w}</div>
                      ))}
                    </div>
                    <div style={{ color: on ? 'var(--cr-primary)' : 'var(--border-strong)' }}><Icon name={on ? 'check-circle' : 'circle'} size={24} /></div>
                  </button>
                );
              })}
            </div>
          ))}

          {entries.length > 0 && horseIds.length > 1 && (
            <>
              <SectionLabel>Packet format</SectionLabel>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <button onClick={() => setFormat('per_horse')} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-primary)', cursor: 'pointer' }}>
                  <div style={{ color: format === 'per_horse' ? 'var(--cr-primary)' : 'var(--text-muted)' }}><Icon name={format === 'per_horse' ? 'radio-on' : 'circle'} size={19} /></div>
                  <div>
                    <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>One PDF per horse</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>What most secretaries want</div>
                  </div>
                </button>
                <button onClick={() => setFormat('combined')} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none', background: 'var(--bg-primary)', cursor: 'pointer' }}>
                  <div style={{ color: format === 'combined' ? 'var(--cr-primary)' : 'var(--text-muted)' }}><Icon name={format === 'combined' ? 'radio-on' : 'circle'} size={19} /></div>
                  <div>
                    <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>One combined PDF</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Single file, grouped by horse</div>
                  </div>
                </button>
              </div>
            </>
          )}

          {entries.length > 0 && (
            <>
              <SectionLabel>Build</SectionLabel>
              <div className="card">
                <input className="form-input" placeholder="Packet title (e.g. Spring Classic)" value={title} onChange={e => setTitle(e.target.value)} />
                <button className="btn btn-primary" style={{ marginTop: 10, width: '100%' }} disabled={building || !selectedEntries.length} onClick={buildPacket}>
                  {building ? 'Building…' : `Build packet${format === 'per_horse' && horseIds.length > 1 ? 's' : ''} · ${selectedEntries.length} document${selectedEntries.length === 1 ? '' : 's'}`}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {built && (
        <>
          <SectionLabel>Ready to go</SectionLabel>
          {(built.warnings || []).length > 0 && (
            <div className="card" style={{ background: 'var(--cr-amber-light)', borderColor: 'var(--cr-amber)' }}>
              {built.warnings.map((w, i) => <div key={i} style={{ fontSize: 12, color: 'var(--cr-amber)' }}>{w}</div>)}
            </div>
          )}
          {built.packets.map(p => (
            <div key={p.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <Tile color="var(--cr-green)" icon="doc" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{p.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{p.page_count} pages</div>
              </div>
              <button className="btn btn-sm btn-secondary" disabled={busyId === p.id} onClick={() => downloadPacket(p)}>Download</button>
            </div>
          ))}

          <button className="card" style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left', cursor: 'pointer', background: 'var(--bg-primary)' }} onClick={() => setEmailOpen(o => !o)}>
            <Tile color="var(--cr-blue)" icon="mail" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Email to secretary</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{built.packets.length} attachment{built.packets.length === 1 ? '' : 's'} in one email</div>
            </div>
            <div style={{ color: 'var(--text-muted)' }}><Icon name={emailOpen ? 'chevron-up' : 'chevron-right'} size={18} /></div>
          </button>

          {emailOpen && (
            <div className="card" style={{ display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select className="form-input" style={{ flex: 1 }} value={sendForm.secretary_id} onChange={e => {
                  const sec = (meta.secretaries || []).find(s => s.id === e.target.value);
                  setSendForm(f => ({ ...f, secretary_id: e.target.value, to_email: sec?.email || f.to_email }));
                }}>
                  <option value="">— Choose a saved secretary —</option>
                  {(meta.secretaries || []).map(s => <option key={s.id} value={s.id}>{s.name}{s.organization ? ` (${s.organization})` : ''}</option>)}
                </select>
                <button type="button" className="btn btn-sm btn-secondary" style={{ whiteSpace: 'nowrap' }} onClick={() => setSecForm(f => ({ ...f, open: !f.open }))}>
                  {secForm.open ? 'Cancel' : '+ New'}
                </button>
              </div>
              {secForm.open && (
                <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: 12, display: 'grid', gap: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Saved to your Secretaries list.</div>
                  <input className="form-input" placeholder="Name *" value={secForm.name} onChange={e => setSecForm(f => ({ ...f, name: e.target.value }))} />
                  <input className="form-input" placeholder="Email *" type="email" value={secForm.email} onChange={e => setSecForm(f => ({ ...f, email: e.target.value }))} />
                  <input className="form-input" placeholder="Phone" value={secForm.phone} onChange={e => setSecForm(f => ({ ...f, phone: e.target.value }))} />
                  <input className="form-input" placeholder="Organization / Show" value={secForm.organization} onChange={e => setSecForm(f => ({ ...f, organization: e.target.value }))} />
                  <div>
                    <button type="button" className="btn btn-sm btn-primary" disabled={secForm.saving} onClick={addSecretary}>{secForm.saving ? 'Saving…' : 'Save secretary'}</button>
                  </div>
                </div>
              )}
              <input className="form-input" placeholder="Or enter email address" value={sendForm.to_email} onChange={e => setSendForm(f => ({ ...f, to_email: e.target.value }))} />
              <input className="form-input" placeholder="Subject" value={sendForm.subject} onChange={e => setSendForm(f => ({ ...f, subject: e.target.value }))} />
              <textarea className="form-input" rows={3} placeholder="Message (optional)" value={sendForm.body} onChange={e => setSendForm(f => ({ ...f, body: e.target.value }))} />
              <button className="btn btn-primary" disabled={sending} onClick={sendPackets}>{sending ? 'Sending…' : `Send ${built.packets.length} packet${built.packets.length === 1 ? '' : 's'}`}</button>
            </div>
          )}
        </>
      )}

      {(meta.packets || []).length > 0 && (
        <>
          <SectionLabel>Packet history</SectionLabel>
          {meta.packets.map(p => (
            <div key={p.id} className="card" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{p.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{fmtDate(p.created_at)} · {p.page_count} pages{p.sent_count ? ` · emailed ${p.sent_count}×` : ''}</div>
              </div>
              <button className="btn btn-sm btn-secondary" disabled={busyId === p.id} onClick={() => downloadPacket(p)}>Download</button>
              <button className="btn btn-sm btn-secondary" disabled={busyId === p.id} onClick={() => deletePacket(p)}>Delete</button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
